import React, { useState } from "react";
import { LessonStep, SimulationTemplateMetadata } from "../../../types/v2";
import { BlockEditor, StepBlock } from "../BlockEditor";
import { activityLabel } from "../../../utils/activityAuthoring";

type Step = Omit<LessonStep, "id"> & { id: number | string };
const field =
  "mt-1 w-full rounded-lg border border-border bg-surface p-3 text-text";
export function SimpleActivityEditor({
  step,
  blocks,
  notes,
  onUpdate,
  onContent,
  onAdvanced,
  onPreview,
  onMaterials,
  templates,
}: {
  step: Step;
  blocks: StepBlock[];
  notes: string;
  onUpdate: (patch: Partial<Step>) => void;
  onContent: (blocks: StepBlock[], notes: string) => void;
  onAdvanced: () => void;
  onPreview: () => void;
  onMaterials: () => void;
  templates: SimulationTemplateMetadata[];
}) {
  const [tab, setTab] = useState("Content");
  const source =
    step.content?.source_item ||
    step.content?.sourceItem ||
    step.signal_config?.source_item;
  const signal = step.signal_config || {};
  const type = source?.type || signal.type || signal.signal_type;
  const pairs: Array<[string, string]> =
    step.content?.authoring_pairs ||
    Object.entries(source?.pairs || {}).map(([a, b]) => [a, String(b)]);
  const savePairs = (next: Array<[string, string]>) =>
    onUpdate({
      content: {
        ...step.content,
        authoring_pairs: next,
        source_item: {
          ...source,
          pairs: Object.fromEntries(next.filter((p) => p[0].trim())),
        },
      },
    });
  const structured = [
    "matching",
    "ordering",
    "drag_drop_fill_blanks",
    "drag_drop_code",
  ].includes(type);
  const simpleSource =
    !source ||
    (structured &&
      (type !== "matching" ||
        (!Array.isArray(source.pairs) &&
          Object.values(source.pairs || {}).every(
            (v) => typeof v === "string",
          ))) &&
      (type !== "ordering" ||
        [source.items, source.answer].every(
          (v) =>
            !v || (Array.isArray(v) && v.every((x) => typeof x === "string")),
        )));
  const supported =
    simpleSource &&
    (!type ||
      [
        "mcq",
        "text",
        "short_answer",
        "matching",
        "ordering",
        "drag_drop_fill_blanks",
        "drag_drop_code",
      ].includes(type));
  const updateSource = (patch: any) => {
    const next = { ...source, ...patch };
    if (Object.prototype.hasOwnProperty.call(patch, "answer_code")) {
      delete next.answer_tokens;
      delete next.answerTokens;
      delete next.answer;
    }
    if (Object.prototype.hasOwnProperty.call(patch, "target"))
      delete next.blanks;
    onUpdate({
      content: { ...step.content, source_item: next },
      signal_config: { ...signal, prompt: next.question || "" },
    });
  };
  const updateSignal = (patch: any) =>
    onUpdate({ signal_config: { ...signal, ...patch } });
  const roleOptions = [
    ["bell_ringer", "Entry ticket"],
    ["instruction", "Instruction"],
    ["practice", "Practice"],
    ["assessment", "Assessment"],
    ["lab", "Lab"],
    ["reflection", "Reflection"],
    ["exit_ticket", "Exit ticket"],
    ["extension", "Extension"],
  ];
  const options: any[] = Array.isArray(signal.options) ? signal.options : [];
  return (
    <div className="space-y-5" aria-label="Simplified activity editor">
      <div className="grid gap-4 md:grid-cols-2">
        <label>
          Activity title
          <input
            className={field}
            value={step.title}
            onChange={(e) => onUpdate({ title: e.target.value })}
          />
        </label>
        <label>
          Role in the lesson
          <select
            className={field}
            value={step.step_kind || "instruction"}
            onChange={(e) => onUpdate({ step_kind: e.target.value })}
          >
            {!roleOptions.some(([v]) => v === step.step_kind) &&
              step.step_kind && (
                <option value={step.step_kind}>{step.step_kind}</option>
              )}
            {roleOptions.map(([v, l]) => (
              <option key={v} value={v}>
                {l}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="flex flex-wrap justify-between gap-2">
        <span className="rounded bg-surface-muted px-3 py-1 text-sm">
          {activityLabel(step)}
        </span>
        <button className="btn-secondary" onClick={onPreview}>
          Preview content
        </button>
      </div>
      {step.activity_format !== "simulation" &&
        (!step.live_response_mode || step.live_response_mode === "none") && (
          <p className="text-sm text-muted">
            No student response required. Present, explain, and move forward
            when ready.
          </p>
        )}
      <div className="flex flex-wrap gap-2" aria-label="Activity editing views">
        {(step.activity_format === "simulation"
          ? ["Content"]
          : ["Content", "Teacher notes"]
        ).map((t) => (
          <button
            key={t}
            className={tab === t ? "btn-primary" : "btn-secondary"}
            aria-pressed={tab === t}
            onClick={() => setTab(t)}
          >
            {t}
          </button>
        ))}
      </div>
      {tab === "Teacher notes" ? (
        <label className="block">
          How I’ll teach this
          <textarea
            className={field}
            rows={8}
            value={notes}
            onChange={(e) => onContent(blocks, e.target.value)}
            placeholder="Explain the idea, model an example and connect it to what students do next."
          />
        </label>
      ) : (
        <>
          {step.activity_format === "simulation" ? (
            <>
              <label className="block">
                Simulation template
                <select
                  className={field}
                  value={step.simulation_template_id || ""}
                  onChange={(e) => {
                    const t = templates.find((x) => x.id === e.target.value);
                    if (t)
                      onUpdate({
                        simulation_template_id: t.id,
                        simulation_objective: t.default_objective,
                      });
                  }}
                >
                  {!templates.some(
                    (t) => t.id === step.simulation_template_id,
                  ) && (
                    <option value={step.simulation_template_id || ""}>
                      Current template:{" "}
                      {step.simulation_template_id || "Unavailable"}
                    </option>
                  )}
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block">
                Student objective
                <textarea
                  className={field}
                  maxLength={500}
                  value={step.simulation_objective || ""}
                  onChange={(e) =>
                    onUpdate({ simulation_objective: e.target.value })
                  }
                />
              </label>
              <p className="text-sm text-muted">
                The supported template controls simulation behavior and safety
                rules.
              </p>
            </>
          ) : (
            <>
              {!supported && (
                <div className="rounded border border-border p-4">
                  <p>
                    This imported response uses additional settings. Its content
                    is preserved.
                  </p>
                  <button className="btn-secondary" onClick={onAdvanced}>
                    Edit advanced response
                  </button>
                </div>
              )}
              {supported && type && (
                <label className="block">
                  Question
                  <textarea
                    className={field}
                    value={
                      structured ? source?.question || "" : signal.prompt || ""
                    }
                    onChange={(e) =>
                      structured
                        ? updateSource({ question: e.target.value })
                        : updateSignal({ prompt: e.target.value })
                    }
                  />
                </label>
              )}
              {supported && type === "mcq" && (
                <div className="space-y-3">
                  <h3>Answer choices</h3>
                  {options.map((option, i) => (
                    <div key={i} className="flex flex-wrap items-center gap-3">
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          name={`correct-${step.id}`}
                          aria-label={`Choice ${i + 1} is correct`}
                          checked={signal.correct_index === i}
                          onChange={() => updateSignal({ correct_index: i })}
                        />
                        Correct
                      </label>
                      <label className="min-w-0 flex-1">
                        Choice {i + 1}
                        <input
                          className={field}
                          value={
                            typeof option === "string"
                              ? option
                              : option.text || ""
                          }
                          onChange={(e) =>
                            updateSignal({
                              options: options.map((v, j) =>
                                j === i
                                  ? typeof v === "string"
                                    ? e.target.value
                                    : { ...v, text: e.target.value }
                                  : v,
                              ),
                            })
                          }
                        />
                      </label>
                      <button
                        className="btn-secondary"
                        disabled={options.length <= 2}
                        aria-label={`Remove choice ${i + 1}`}
                        onClick={() => {
                          const next: Record<string, any> = {
                            ...signal,
                            options: options.filter((_, j) => j !== i),
                          };
                          if (signal.correct_index === i)
                            delete next.correct_index;
                          else if (signal.correct_index > i)
                            next.correct_index--;
                          onUpdate({ signal_config: next });
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                  <button
                    className="btn-secondary"
                    onClick={() => updateSignal({ options: [...options, ""] })}
                  >
                    Add choice
                  </button>
                </div>
              )}
              {supported && type === "matching" && (
                <div className="space-y-3">
                  <h3>Matching pairs</h3>
                  {pairs.map(([left, right], i) => (
                    <div key={i} className="grid gap-3 sm:grid-cols-2">
                      <label>
                        Term {i + 1}
                        <input
                          className={field}
                          value={left}
                          onChange={(e) =>
                            savePairs(
                              pairs.map((p, j) =>
                                j === i ? [e.target.value, p[1]] : p,
                              ),
                            )
                          }
                        />
                      </label>
                      <label>
                        Match {i + 1}
                        <input
                          className={field}
                          value={right}
                          onChange={(e) =>
                            savePairs(
                              pairs.map((p, j) =>
                                j === i ? [p[0], e.target.value] : p,
                              ),
                            )
                          }
                        />
                      </label>
                      <button
                        className="btn-secondary"
                        onClick={() =>
                          savePairs(pairs.filter((_, j) => j !== i))
                        }
                      >
                        Remove pair {i + 1}
                      </button>
                    </div>
                  ))}
                  <button
                    className="btn-secondary"
                    onClick={() => savePairs([...pairs, ["", ""]])}
                  >
                    Add pair
                  </button>
                  <p className="text-sm text-muted">
                    Complete both sides and use a different term for each pair
                    before saving.
                  </p>
                </div>
              )}
              {supported && type === "ordering" && (
                <label className="block">
                  Correct order — one item per line
                  <textarea
                    className={field}
                    rows={6}
                    value={(source?.answer || source?.items || []).join("\n")}
                    onChange={(e) => {
                      const answer = e.target.value.split("\n");
                      updateSource({ items: answer, answer });
                    }}
                  />
                  <span className="text-sm text-muted">
                    Students arrange these items in the correct sequence.
                  </span>
                </label>
              )}
              {supported &&
                ["drag_drop_fill_blanks", "drag_drop_code"].includes(type) && (
                  <div className="space-y-3">
                    <label className="block">
                      Template — use ____ for each blank
                      <textarea
                        className={field}
                        value={source?.target || ""}
                        onChange={(e) =>
                          updateSource({ target: e.target.value })
                        }
                      />
                    </label>
                    <label className="block">
                      Token bank — one token per line
                      <textarea
                        className={field}
                        value={(source?.tokens || []).join("\n")}
                        onChange={(e) =>
                          updateSource({ tokens: e.target.value.split("\n") })
                        }
                      />
                    </label>
                    <label className="block">
                      Completed answer
                      <textarea
                        className={field}
                        value={source?.answer_code || ""}
                        onChange={(e) =>
                          updateSource({ answer_code: e.target.value })
                        }
                      />
                    </label>
                  </div>
                )}
              <div className="space-y-3">
                <h3>
                  {type
                    ? "Student instructions and materials"
                    : "Student-facing explanation and materials"}
                </h3>
                <div className="flex flex-wrap gap-2">
                  <button
                    className="btn-secondary"
                    onClick={() =>
                      onContent([...blocks, { type: "text", text: "" }], notes)
                    }
                  >
                    + Text
                  </button>
                  <button
                    className="btn-secondary"
                    onClick={() =>
                      onContent(
                        [...blocks, { type: "image", src: "", alt: "" }],
                        notes,
                      )
                    }
                  >
                    + Image
                  </button>
                  <button
                    className="btn-secondary"
                    onClick={() =>
                      onContent(
                        [
                          ...blocks,
                          {
                            type: "video",
                            src: "",
                            caption: "",
                            transcript: "",
                          },
                        ],
                        notes,
                      )
                    }
                  >
                    + Video
                  </button>
                  <button
                    className="btn-secondary"
                    onClick={() =>
                      onContent(
                        [...blocks, { type: "link", url: "", label: "" }],
                        notes,
                      )
                    }
                  >
                    + Link
                  </button>
                  <button className="btn-secondary" onClick={onMaterials}>
                    Drive slides / materials
                  </button>
                </div>
                <BlockEditor
                  blocks={blocks}
                  onChange={(next) => onContent(next, notes)}
                />
              </div>
            </>
          )}
        </>
      )}
      <details className="border-t border-border pt-3">
        <summary className="cursor-pointer">Advanced settings</summary>
        <p className="my-3 text-sm text-muted">
          Hook alignment, response behavior, evidence, timing and existing
          support settings. Opening these settings does not change the lesson.
        </p>
        <button className="btn-secondary" onClick={onAdvanced}>
          Open full settings
        </button>
      </details>
    </div>
  );
}
